/* import __COLOCATED_TEMPLATE__ from './audience-selector.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */
/* eslint-disable @intercom/intercom/no-default-task-ember-concurrency */

import Component from '@glimmer/component';
import type Store from '@ember-data/store';
import { inject as service } from '@ember/service';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import { action } from '@ember/object';
import type AiContentSegment from 'embercom/models/ai-content-segment';
import { tracked } from '@glimmer/tracking';
import { helper } from '@ember/component/helper';
import { type TaskGenerator } from 'ember-concurrency';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type SuggestionSegmentationRule from 'embercom/models/knowledge-base/suggestion-segmentation-rule';
import { CAN_MANAGE_KNOWLEDGE_BASE_CONTENT } from 'embercom/lib/knowledge-hub/constants';
import { all } from 'rsvp';

interface Args {}

export default class AudienceSelector extends Component<Args> {
  @service declare store: Store;
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare intl: $TSFixMe;
  @service declare permissionsService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  @tracked loadingSegmentationRuleId: string | null = null;
  @tracked suggestionSegmentationRules: Array<SuggestionSegmentationRule> = [];

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.aiContentSegmentsService.loadSegments();
    this.loadSuggestionSegmentationRules();
  }

  get isLoading() {
    return (
      taskFor(this.loadSuggestionSegmentationRulesTask).isRunning ||
      this.aiContentSegmentsService.isLoading
    );
  }

  get aiContentSegments() {
    return this.aiContentSegmentsService.segments;
  }

  get selectedSegmentIds(): Array<string> {
    return this.aiContentSegments
      .filter((segment) =>
        this.suggestionSegmentationRules.find((rule) => rule.aiContentSegmentId === segment.id),
      )
      .map((segment) => segment.id);
  }

  get dropdownLabel() {
    if (this.selectedSegmentIds.length === 0) {
      return this.intl.t('knowledge-hub.content-editor.shared.audience-section.dropdown.everyone');
    }

    let selectedSegments = this.selectedSegmentIds
      .map((id) => this.aiContentSegments.find((segment) => segment.id === id)?.nameWithFallback)
      .filter(Boolean);

    if (selectedSegments.length <= 2) {
      return selectedSegments.join(', ');
    }

    let firstTwo = selectedSegments.slice(0, 2);
    let remainingCount = selectedSegments.length - 2;
    return `${firstTwo.join(', ')} + ${remainingCount} more`;
  }

  segmentHasSegmentationRule = helper(function ([segment, selectedSegmentIds]: [
    AiContentSegment,
    Array<string>,
  ]) {
    return selectedSegmentIds.includes(segment.id);
  });

  async loadSuggestionSegmentationRules() {
    return taskFor(this.loadSuggestionSegmentationRulesTask).perform();
  }

  @action
  async updateSegmentationRule(segment: AiContentSegment) {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e) {
      return;
    }

    this.loadingSegmentationRuleId = segment.id;
    try {
      if (this.selectedSegmentIds.includes(segment.id)) {
        await taskFor(this.deleteSegmentationRuleTask).perform(segment);
        this.intercomEventService.trackAnalyticsEvent({
          action: 'clicked',
          object: 'audience_selector',
          segment_id: segment.id,
          rule_action: 'deleted',
        });
      } else {
        await taskFor(this.createSegmentationRuleTask).perform(segment);
        this.intercomEventService.trackAnalyticsEvent({
          action: 'clicked',
          object: 'audience_selector',
          segment_id: segment.id,
          rule_action: 'created',
        });
      }
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.suggestions.settings.error-message'),
      );
    } finally {
      await this.resetSegmentationRules();
    }
  }

  @action
  async deleteAllSuggestionSegmentationRules() {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e) {
      return;
    }

    this.loadingSegmentationRuleId = 'everyone';
    let selectedSegments = this.aiContentSegments.filter((segment) =>
      this.selectedSegmentIds.includes(segment.id),
    );
    try {
      await all(
        selectedSegments.map((segment) =>
          taskFor(this.deleteSegmentationRuleTask).perform(segment),
        ),
      );
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'audience_selector',
        rule_action: 'deleted_all',
      });
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.suggestions.settings.error-message'),
      );
    } finally {
      await this.resetSegmentationRules();
    }
  }

  @task
  *createSegmentationRuleTask(segment: AiContentSegment): TaskGenerator<void> {
    let segmentationRule = this.store.createRecord('knowledge-base/suggestion-segmentation-rule', {
      title: segment.nameWithFallback,
      aiContentSegmentId: segment.id,
    });
    yield segmentationRule.save();
  }

  @task
  *deleteSegmentationRuleTask(segment: AiContentSegment): TaskGenerator<void> {
    let segmentationRule = this.suggestionSegmentationRules.find(
      (rule) => rule.aiContentSegmentId === segment.id,
    );
    yield segmentationRule?.destroyRecord();
  }

  @task
  *loadSuggestionSegmentationRulesTask(): TaskGenerator<void> {
    let results = yield this.store.findAll('knowledge-base/suggestion-segmentation-rule');
    this.suggestionSegmentationRules = results.toArray();
  }

  private async resetSegmentationRules() {
    await this.loadSuggestionSegmentationRules();
    this.notificationsService.notifyConfirmation(
      this.intl.t('ai-agent.suggestions.settings.success-message'),
    );
    this.loadingSegmentationRuleId = null;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Suggestions::AudienceSelector': typeof AudienceSelector;
  }
}
